import React, { useEffect, useState } from "react";
import { CalendarRange, Award } from "lucide-react";
import { api } from "../../lib/api";
import { fmtKwh } from "../../lib/format";

// Data-source palette — dot colour + human label + tooltip copy. Match
// the backend's `_pick_canonical_source` priority list so the UI never
// shows a colour the API doesn't emit.
const SOURCE_META = {
  meteronline:          { color: "#059669", label: "MeterOnline",        tip: "Live HH revenue meter (highest audit trust)." },
  meteronline_backfill: { color: "#10b981", label: "MeterOnline backfill", tip: "Historic MeterOnline pull (revenue meter, backdated)." },
  solis:                { color: "#0284c7", label: "Solis",               tip: "Solis inverter cloud." },
  solis_15min:          { color: "#0284c7", label: "Solis 15-min",        tip: "Solis 15-min live sync." },
  solaredge_api:        { color: "#f97316", label: "SolarEdge",           tip: "SolarEdge inverter cloud (daily sync)." },
  solaredge_daily:      { color: "#f97316", label: "SolarEdge daily",     tip: "SolarEdge daily rollup." },
  csv_import:           { color: "#eab308", label: "CSV import",          tip: "User-uploaded CSV / Excel." },
  legacy_daily:         { color: "#94a3b8", label: "Legacy daily",        tip: "Historic daily-rollup, pre-15-min era." },
  synthetic:            { color: "#a3a3a3", label: "Synthetic",           tip: "Development / seed data — not a real reading." },
  "15min":              { color: "#7c3aed", label: "15-min sample",       tip: "Untagged 15-min sample." },
  unknown:              { color: "#d4d4d4", label: "Unknown",              tip: "No provenance tag on the underlying rows." },
};

function SourceDot({ source, size = 8, className = "" }) {
  const meta = SOURCE_META[source] || SOURCE_META.unknown;
  return (
    <span
      className={`inline-block rounded-full align-middle ${className}`}
      style={{ width: size, height: size, background: meta.color }}
      title={`${meta.label} · ${meta.tip}`}
      data-testid={`source-dot-${source}`}
    />
  );
}

/**
 * "This year" dashboard for a single site.
 *
 * 12-row table — one per month of the current calendar year — with generation
 * (kWh), export (kWh), cumulative generation, and REGO credits earned per
 * month + cumulative REGOs. The REGO column is the money view for asset
 * managers: 1 MWh of generation = 1 REGO credit (UK Ofgem definition).
 *
 * Cumulative REGO math correctly handles month-boundary rollover — e.g. two
 * months of 700 kWh each award 1 REGO in the second month (crosses 1 MWh
 * cumulative) rather than 0 in each month.
 *
 * Each Actual figure carries a coloured provenance dot so asset managers
 * can see at a glance whether the month's number came from a revenue
 * meter, the inverter cloud, a CSV import, or synthetic seed data — the
 * kind of paper trail Ofgem asks for during REGO audits.
 */
export default function MonthlySummary({ projectId }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getUTCFullYear());
  // Include descendant sub-sites in the roll-up. Persisted per project
  // so parent-site views default to "on" once the user has enabled it.
  const _lsKey = `solaros.monthly.includeChildren.${projectId}`;
  const [includeChildren, setIncludeChildren] = useState(() => {
    try { return window.localStorage.getItem(_lsKey) === "1"; } catch { return false; }
  });
  const [hasChildren, setHasChildren] = useState(false);

  useEffect(() => {
    // Quick check for children — controls whether the toggle even renders.
    api.get(`/projects/${projectId}/children`)
      .then(r => setHasChildren((r.data || []).length > 0))
      .catch(() => setHasChildren(false));
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inc = includeChildren && hasChildren ? "&include_children=1" : "";
        const { data } = await api.get(
          `/monitoring/${projectId}/monthly-summary?year=${year}${inc}`);
        if (!cancelled) setData(data);
      } catch { if (!cancelled) setData(null); }
    })();
    return () => { cancelled = true; };
  }, [projectId, year, includeChildren, hasChildren]);

  useEffect(() => {
    try { window.localStorage.setItem(_lsKey, includeChildren ? "1" : "0"); } catch { /* ignore */ }
  }, [includeChildren, _lsKey]);

  if (!data) return null;
  const currentYear = new Date().getUTCFullYear();

  return (
    <section className="swiss-card p-6 mt-4" data-testid="monthly-summary-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label flex items-center gap-2">
            <CalendarRange size={11} /> This year · monthly summary
          </div>
          <div className="font-display text-xl font-semibold mt-1">
            {year} · {data.months.length} months
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChildren && (
            <label className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest cursor-pointer px-2 py-1 border border-[var(--line)] bg-white hover:border-[var(--accent)]"
                   title="Sum every sub-site (unit / building) into the total"
                   data-testid="monthly-include-children-toggle">
              <input type="checkbox" checked={includeChildren}
                     onChange={(e) => setIncludeChildren(e.target.checked)} />
              Include sub-sites
              {data?.include_children && data?.child_ids?.length > 0 && (
                <span className="text-[9px] muted normal-case tracking-normal">
                  ({data.child_ids.length} unit{data.child_ids.length === 1 ? "" : "s"})
                </span>
              )}
            </label>
          )}
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  className="input-swiss text-xs py-1 font-mono"
                  data-testid="monthly-summary-year-select">
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Totals band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mb-4 bg-[var(--surface-2)]"
           data-testid="monthly-summary-totals">
        <TotalCell label="Total generation" value={fmtKwh(data.total_generation_kwh)} testid="total-gen" />
        <TotalCell label="Total actual" value={fmtKwh(data.total_actual_kwh)} testid="total-actual" />
        <TotalCell label="REGOs earned" value={data.total_regos.toLocaleString()}
                   accent testid="total-regos" icon={<Award size={13} />} />
        <TotalCell label="Avg per month"
                   value={fmtKwh(data.total_generation_kwh / 12)}
                   testid="total-avg" />
      </div>

      {/* 12-row table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="monthly-summary-table">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-widest muted border-b border-[var(--line)]">
              <th className="text-left py-2 px-3">Month</th>
              <th className="text-right py-2 px-3">Generation</th>
              <th className="text-right py-2 px-3">Actual</th>
              <th className="text-right py-2 px-3">Export</th>
              <th className="text-right py-2 px-3">Cumulative gen</th>
              <th className="text-right py-2 px-3">REGOs</th>
              <th className="text-right py-2 px-3">Cumulative REGOs</th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((m) => (
              <tr key={m.month} className="border-b border-[var(--line)] hover:bg-[var(--surface-2)]"
                  data-testid={`monthly-row-${m.month}`}>
                <td className="py-2 px-3 font-mono text-xs">{m.label} <span className="muted">{m.month}</span></td>
                <td className="py-2 px-3 text-right font-mono">{m.generation_kwh > 0 ? fmtKwh(m.generation_kwh) : "—"}</td>
                <td className="py-2 px-3 text-right font-mono muted">
                  {m.actual_kwh > 0 ? (
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      {fmtKwh(m.actual_kwh)}
                      {m.actual_source && <SourceDot source={m.actual_source} />}
                    </span>
                  ) : "—"}
                </td>
                <td className="py-2 px-3 text-right font-mono muted">{m.export_kwh > 0 ? fmtKwh(m.export_kwh) : "—"}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtKwh(m.cumulative_generation_kwh)}</td>
                <td className={`py-2 px-3 text-right font-mono font-semibold ${m.regos_earned > 0 ? "text-emerald-700" : "muted"}`}>
                  {m.regos_earned > 0 ? `+${m.regos_earned}` : "—"}
                </td>
                <td className="py-2 px-3 text-right font-mono font-bold">{m.cumulative_regos.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Provenance legend — one badge per distinct source that contributed
          a row this year. Gives Ofgem auditors an at-a-glance trail. */}
      {Array.isArray(data.sources) && data.sources.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]"
             data-testid="monthly-source-legend">
          <span className="muted font-mono uppercase tracking-widest">Data sources this year:</span>
          {data.sources.map((src) => {
            const meta = SOURCE_META[src] || SOURCE_META.unknown;
            return (
              <span key={src} className="inline-flex items-center gap-1.5"
                    data-testid={`source-legend-${src}`}
                    title={meta.tip}>
                <SourceDot source={src} />
                <span>{meta.label}</span>
              </span>
            );
          })}
        </div>
      )}

      <p className="text-[11px] muted mt-3 flex items-center gap-1.5">
        <Award size={11} className="text-emerald-600" />
        REGO (Renewable Energy Guarantees of Origin) — Ofgem UK. 1 REGO = 1 MWh of eligible renewable generation. Cumulative counter awards a REGO the moment total gen crosses each MWh boundary, so partial-month generation isn&apos;t lost across the calendar break.
      </p>
    </section>
  );
}

function TotalCell({ label, value, testid, accent = false, icon = null }) {
  return (
    <div className={`p-4 border ${accent
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : "bg-white border-[var(--line)]"}`}
         data-testid={testid}>
      <div className="label flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-display text-xl font-bold mt-1 font-mono">{value}</div>
    </div>
  );
}
